// LocoBridge shared domain types. Pure TypeScript (no DOM / no Workers globals)
// so it can be imported by the frontend, the API, and the test suite alike.

// ---------------------------------------------------------------------------
// Roles & permissions
// ---------------------------------------------------------------------------
export const ROLES = ["owner", "admin", "finance", "sales", "partner", "viewer", "tax"] as const;
export type Role = (typeof ROLES)[number];

export const ROLE_LABELS: Record<Role, string> = {
  owner: "オーナー",
  admin: "管理者",
  finance: "経理",
  sales: "営業",
  partner: "紹介者",
  viewer: "閲覧者",
  tax: "税理士",
};

// ---------------------------------------------------------------------------
// Deal status (案件ステータス)
// ---------------------------------------------------------------------------
export const DEAL_STATUSES = [
  "referral_received", // 紹介受付
  "before_first_contact", // 初回連絡前
  "first_contacted", // 初回連絡済み
  "meeting_scheduled", // 商談予定
  "met", // 商談済み
  "proposing", // 提案中
  "quote_submitted", // 見積提出済み
  "contract_pending", // 契約予定
  "contracted", // 契約済み
  "awaiting_payment", // 入金待ち
  "paid_in", // 入金済み
  "payout_review", // 支払判定待ち
  "payout_scheduled", // 紹介料支払予定
  "payout_done", // 紹介料支払済み
  "on_hold", // 保留
  "lost", // 失注
  "canceled", // キャンセル
  "refunding", // 返金対応中
  "subsidy_rejected", // 補助金不採択
  "trouble", // トラブル対応中
  "completed", // 完了
  "archived", // アーカイブ
] as const;
export type DealStatus = (typeof DEAL_STATUSES)[number];

export const DEAL_STATUS_LABELS: Record<DealStatus, string> = {
  referral_received: "紹介受付",
  before_first_contact: "初回連絡前",
  first_contacted: "初回連絡済み",
  meeting_scheduled: "商談予定",
  met: "商談済み",
  proposing: "提案中",
  quote_submitted: "見積提出済み",
  contract_pending: "契約予定",
  contracted: "契約済み",
  awaiting_payment: "入金待ち",
  paid_in: "入金済み",
  payout_review: "支払判定待ち",
  payout_scheduled: "紹介料支払予定",
  payout_done: "紹介料支払済み",
  on_hold: "保留",
  lost: "失注",
  canceled: "キャンセル",
  refunding: "返金対応中",
  subsidy_rejected: "補助金不採択",
  trouble: "トラブル対応中",
  completed: "完了",
  archived: "アーカイブ",
};

// Statuses that lock amount changes (要件: 契約済/入金済/支払確定/支払済/締め済).
export const AMOUNT_LOCKING_STATUSES: DealStatus[] = [
  "contracted",
  "awaiting_payment",
  "paid_in",
  "payout_review",
  "payout_scheduled",
  "payout_done",
  "completed",
];

// Statuses that forbid paying commission (キャンセル・失注・返金・トラブル).
export const PAYOUT_BLOCKING_STATUSES: DealStatus[] = [
  "lost",
  "canceled",
  "refunding",
  "trouble",
  "subsidy_rejected",
];

// ---------------------------------------------------------------------------
// Confirmation status (税理士/弁護士/契約書 等の確認ステータス)
// ---------------------------------------------------------------------------
export const CONFIRMATION_STATUSES = ["unconfirmed", "confirmed", "not_required"] as const;
export type ConfirmationStatus = (typeof CONFIRMATION_STATUSES)[number];

export const CONFIRMATION_LABELS: Record<ConfirmationStatus, string> = {
  unconfirmed: "未確認",
  confirmed: "確認済み",
  not_required: "確認不要",
};

// ---------------------------------------------------------------------------
// Approval
// ---------------------------------------------------------------------------
export const APPROVAL_ACTIONS = [
  "activate_deal", // 案件作成後の有効化
  "confirm_contract_amount", // 契約金額の確定
  "confirm_commission", // 紹介料の確定
  "confirm_tusg_fee", // TUSG管理費の確定
  "confirm_dev_fee", // 開発保守費の確定
  "confirm_payment_in", // 入金確認
  "confirm_eligibility", // 支払可否の確定
  "mark_paid_out", // 支払済み処理
  "unlock_amount", // 金額変更ロック解除
  "change_after_closing", // 月次締め後の変更
  "change_bank_account", // 振込先変更
  "delete_file", // ファイル削除
  "hard_delete", // 物理削除
  "refund_or_cancel", // 返金・キャンセル処理
  "duplicate_payment_exception", // 二重支払い例外処理
] as const;
export type ApprovalAction = (typeof APPROVAL_ACTIONS)[number];

export const APPROVAL_STATUSES = ["pending", "approved", "rejected"] as const;
export type ApprovalStatus = (typeof APPROVAL_STATUSES)[number];

// ---------------------------------------------------------------------------
// Commission rules (報酬ルール)
// ---------------------------------------------------------------------------
export const COMMISSION_RULE_TYPES = [
  "fixed", // 固定紹介料
  "percent", // パーセント紹介料
  "by_industry", // 業種別紹介料
  "by_amount", // 案件金額別紹介料（丸めルール含む）
  "special", // 紹介者別特別条件
] as const;
export type CommissionRuleType = (typeof COMMISSION_RULE_TYPES)[number];

export interface CommissionRuleConfig {
  // common modifiers
  cap?: number | null; // 上限金額
  minPayout?: number | null; // 最低支払金額
  roundTo?: number | null; // 丸め単位（例 10000）
  // fixed
  amount?: number | null;
  // percent
  percent?: number | null; // 0..100
  base?: "contract" | "net" | "subsidy"; // 率の対象（既定 contract）
  // by_industry: { 業種: 固定額 or 率 }
  industries?: Record<string, { amount?: number; percent?: number }>;
  // by_amount: 金額帯テーブル（昇順）。min<=金額 で当てはまる最後の行を採用
  tiers?: Array<{ minAmount: number; amount?: number; percent?: number }>;
}

export interface CommissionRule {
  id: string;
  org_id: string;
  name: string;
  type: CommissionRuleType;
  config: CommissionRuleConfig;
  // policy flags（要件）
  pay_only_after_payment_in: boolean; // 入金確認後のみ支払対象
  stop_on_cancel: boolean; // キャンセル時の支払停止
  refund_behavior: "return" | "offset" | "none"; // 返金時の返還/相殺
  subsidy_rejected_behavior: "stop" | "pay" | "manual"; // 補助金不採択時の扱い
  active: boolean;
}

// ---------------------------------------------------------------------------
// Payment eligibility input/output (支払可否判定)
// ---------------------------------------------------------------------------
export interface EligibilityInput {
  dealStatus: DealStatus;
  // evidence files present
  hasContractFile: boolean; // 契約書あり
  hasInvoiceFile: boolean; // 請求書あり
  hasReferralEvidence: boolean; // 紹介証跡あり
  // money / process
  camomilePaymentConfirmed: boolean; // camomile入金確認済み
  tusgInvoiceIssued: boolean; // TUSG請求書発行済み
  bankAccountRegistered: boolean; // 支払先情報登録済み
  bankChangedRecently: boolean; // 振込先変更直後か
  bankChangeApproved: boolean; // その変更が承認済みか
  // confirmation statuses
  taxAdvisorStatus: ConfirmationStatus; // 税理士確認
  lawyerStatus: ConfirmationStatus; // 弁護士確認
  // partner consents / contract
  referralContractSigned: boolean; // 紹介契約締結済み
  consentPersonalInfo: boolean; // 個人情報利用同意
  consentPaymentTerms: boolean; // 支払条件同意
  // closing / double-pay
  monthlyClosingBlocked: boolean; // 月次締め上の問題あり
  alreadyPaid: boolean; // 同一案件で支払済み（二重支払い）
}

// 選択肢（プルダウン用）。ok=true の値が選ばれていれば「満たす」と判定。
export interface RequirementOption {
  label: string;
  ok: boolean;
}

export interface EligibilityCheck {
  id?: string; // payment_requirements.id（手動チェックの対象特定に使用）
  code: string;
  kind?: "auto" | "manual";
  label: string;
  ok: boolean;
  value_type?: "check" | "select" | "text"; // 入力タイプ（既定: check）
  options?: RequirementOption[];   // value_type='select' の選択肢
  select_value?: string | null;    // 案件で選ばれた値（select）
  due_date?: string | null;  // 予定日／締切（案件ごと）
  done_date?: string | null; // 完了日／登録日（案件ごと）
  note?: string | null;      // メモ（案件ごと）
}

export interface EligibilityResult {
  eligible: boolean;
  checks: EligibilityCheck[];
  blockers: string[]; // labels of failed checks（足りない項目）
}

// 支払可否チェック項目（組織ごとにカスタマイズ可能な定義 + 案件側の手動チェック状態）
export interface EligibilityRequirement {
  id?: string;
  code: string; // 組込コード（auto）または 'custom'
  kind: "auto" | "manual";
  label: string;
  enabled?: boolean; // false の項目は判定から除外
  checked?: boolean; // kind='manual' の案件側チェック状態
  value_type?: "check" | "select" | "text"; // 入力タイプ（既定: check）
  options?: RequirementOption[];   // value_type='select' の選択肢
  select_value?: string | null;    // 案件で選ばれた値（select）
  due_date?: string | null;  // 予定日／締切（案件ごと）
  done_date?: string | null; // 完了日／登録日（案件ごと）
  note?: string | null;      // メモ（案件ごと）
}

// ---------------------------------------------------------------------------
// 営業管理（Sales Hub 統合）
// ---------------------------------------------------------------------------
export const OPPORTUNITY_STATUSES = [
  "proposing", // 提案中
  "negotiating", // 交渉中
  "verbal", // 内諾
  "awaiting", // 回答待ち
  "won", // 成約
  "lost", // 失注
] as const;
export type OpportunityStatus = (typeof OPPORTUNITY_STATUSES)[number];
export const OPPORTUNITY_STATUS_LABELS: Record<OpportunityStatus, string> = {
  proposing: "提案中",
  negotiating: "交渉中",
  verbal: "内諾",
  awaiting: "回答待ち",
  won: "成約",
  lost: "失注",
};

// 日次グリッドの指標（8項目）
export const SALES_METRICS = [
  "contracts", // 契約数
  "meo", // MEO商談
  "delivery", // デリバリー商談
  "subsidy", // 補助金相談
  "awaiting", // 回答待ち
  "appointments", // アポ獲得
  "calls", // コール
  "doorknocks", // 飛び込み
] as const;
export type SalesMetric = (typeof SALES_METRICS)[number];
export const SALES_METRIC_LABELS: Record<SalesMetric, string> = {
  contracts: "契約数",
  meo: "MEO商談",
  delivery: "デリバリー商談",
  subsidy: "補助金相談",
  awaiting: "回答待ち",
  appointments: "アポ獲得",
  calls: "コール",
  doorknocks: "飛び込み",
};
// 月間目標を持つ指標（回答待ちは目標なし）
export const SALES_GOAL_METRICS: SalesMetric[] = [
  "contracts",
  "meo",
  "delivery",
  "subsidy",
  "appointments",
  "calls",
  "doorknocks",
];

// ---------------------------------------------------------------------------
// Misc shared constants
// ---------------------------------------------------------------------------
export const DEFAULT_REFERRAL_VALIDITY_DAYS = 90;
export const DEFAULT_BANK_CHANGE_WARNING_HOURS = 72;

export const LEGAL_NOTICE =
  "本システムは案件管理、紹介者管理、請求・入金・支払予定管理を補助するための業務管理ツールです。" +
  "税務判断、法的判断、補助金採択可否、会計処理の正確性を保証するものではありません。" +
  "最終判断は税理士、弁護士、社労士、行政書士などの専門家に確認してください。";
